import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Form, Input, Select, InputNumber, DatePicker, Popconfirm } from "antd";
import { CiSearch } from "react-icons/ci";
import { FaPlusCircle } from "react-icons/fa";
import { MdEdit, MdRemoveRedEye, MdDelete } from "react-icons/md";
import dayjs from "dayjs";
import Sidebar from "../../../../Components/Common/Sidebar";
import TableShimmer from "../../../../Components/Common/TableShimmer.jsx";
import {
  fetchCashFlowsThunk,
  createCashFlowThunk,
  updateCashFlowThunk,
  deleteCashFlowThunk,
} from "../../../../Store/Slices/Finance/cashFlow/cashFlow.thunk";
import { fetchEntity } from "../../../../Store/Slices/Finance/entitie/entity.thunk";
import ProtectedAction from "../../../../Routes/ProtectedRoutes/ProtectedAction.js";
import { PERMISSIONS } from "../../../../config/permission";

const { Option } = Select;

const transactionTypeOptions = [
  { value: "cash_to_account", label: "Cash → Account", fromType: "cash", toType: "account" },
  { value: "account_to_cash", label: "Account → Cash", fromType: "account", toType: "cash" },
  { value: "account_to_account", label: "Account → Account", fromType: "account", toType: "account" },
  { value: "check_to_account", label: "Check → Account", fromType: "check", toType: "account" },
  { value: "check_to_cash", label: "Check → Cash", fromType: "check", toType: "cash" },
  { value: "account_to_check", label: "Account → Check", fromType: "account", toType: "check" },
];

const typeLabels = {
  cash: "Cash",
  account: "Account",
  check: "Check",
};

const typeColors = {
  cash: "bg-green-50 text-green-600 border-green-100",
  account: "bg-blue-50 text-blue-600 border-blue-100",
  check: "bg-orange-50 text-orange-600 border-orange-100",
};

const txnColors = {
  cash_to_account: "bg-purple-50 text-purple-600 border-purple-100",
  account_to_cash: "bg-indigo-50 text-indigo-600 border-indigo-100",
  account_to_account: "bg-blue-50 text-blue-600 border-blue-100",
  check_to_account: "bg-yellow-50 text-yellow-600 border-yellow-100",
  check_to_cash: "bg-orange-50 text-orange-600 border-orange-100",
  account_to_check: "bg-pink-50 text-pink-600 border-pink-100",
};

const VoucherForm = ({ onClose, editData, bankEntities }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { loading } = useSelector((s) => s.admin.cashFlow);
  const activeFinancialYear = useSelector((s) => s.common.financialYear?.activeYear);
  const selectedAcademicYear = useSelector((s) => s.common.academicYear?.seletedAcademicYear);
  const viewMode = editData?.mode === "view";

  const [fromType, setFromType] = useState("");
  const [toType, setToType] = useState("");

  useEffect(() => {
    if (editData && editData.mode !== "view") {
      const txn = transactionTypeOptions.find((t) => t.value === editData.transactionType);
      if (txn) {
        setFromType(txn.fromType);
        setToType(txn.toType);
      }
      form.setFieldsValue({
        transactionType: editData.transactionType,
        amount: editData.amount,
        transactionId: editData.transactionId,
        note: editData.note,
        transactionDate: editData.transactionDate ? dayjs(editData.transactionDate) : null,
        fromEntityId: editData.from?.entityId?._id || editData.from?.entityId || null,
        toEntityId: editData.to?.entityId?._id || editData.to?.entityId || null,
      });
    } else if (editData?.mode === "view") {
      const txn = transactionTypeOptions.find((t) => t.value === editData.transactionType);
      if (txn) {
        setFromType(txn.fromType);
        setToType(txn.toType);
      }
      form.setFieldsValue({
        transactionType: editData.transactionType,
        amount: editData.amount,
        transactionId: editData.transactionId,
        note: editData.note,
        transactionDate: editData.transactionDate ? dayjs(editData.transactionDate) : null,
        fromEntityId: editData.from?.entityId?._id || editData.from?.entityId || null,
        toEntityId: editData.to?.entityId?._id || editData.to?.entityId || null,
      });
    } else {
      form.resetFields();
      setFromType("");
      setToType("");
    }
  }, [editData, form]);

  const handleTxnTypeChange = (value) => {
    const txn = transactionTypeOptions.find((t) => t.value === value);
    if (txn) {
      setFromType(txn.fromType);
      setToType(txn.toType);
    }
    form.setFieldsValue({ fromEntityId: null, toEntityId: null });
  };

  const handleSubmit = async (values) => {
    const txn = transactionTypeOptions.find((t) => t.value === values.transactionType);
    const payload = {
      transactionType: values.transactionType,
      from: {
        type: txn?.fromType,
        entityId: values.fromEntityId || null,
      },
      to: {
        type: txn?.toType,
        entityId: values.toEntityId || null,
      },
      amount: values.amount,
      transactionId: values.transactionId,
      note: values.note,
      transactionDate: values.transactionDate?.toISOString(),
      financialYear: activeFinancialYear?._id,
      academicYear: selectedAcademicYear?._id,
    };

    if (editData && editData.mode !== "view") {
      await dispatch(updateCashFlowThunk({ id: editData._id, data: payload }));
    } else {
      await dispatch(createCashFlowThunk(payload));
    }
    onClose();
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <p className="text-gray-400 text-xs mb-4">Transaction Info</p>

      <Form.Item
        name="transactionType"
        label="Transaction Type"
        rules={[{ required: true, message: "Please select a transaction type" }]}
      >
        <Select
          disabled={viewMode}
          placeholder="Select transaction type"
          onChange={handleTxnTypeChange}
          options={transactionTypeOptions.map((t) => ({ label: t.label, value: t.value }))}
        />
      </Form.Item>

      <div className="grid grid-cols-2 gap-4">
        <Form.Item label="From">
          <Input
            disabled
            value={fromType ? typeLabels[fromType] || fromType : "—"}
            className="bg-gray-50"
          />
        </Form.Item>
        <Form.Item label="To">
          <Input
            disabled
            value={toType ? typeLabels[toType] || toType : "—"}
            className="bg-gray-50"
          />
        </Form.Item>
      </div>

      {fromType === "account" && (
        <Form.Item name="fromEntityId" label="From Bank Account">
          <Select
            disabled={viewMode}
            placeholder="Select bank entity"
            allowClear
            showSearch
            optionFilterProp="label"
            options={bankEntities.map((e) => ({ label: e.entityName, value: e._id }))}
          />
        </Form.Item>
      )}

      {toType === "account" && (
        <Form.Item name="toEntityId" label="To Bank Account">
          <Select
            disabled={viewMode}
            placeholder="Select bank entity"
            allowClear
            showSearch
            optionFilterProp="label"
            options={bankEntities.map((e) => ({ label: e.entityName, value: e._id }))}
          />
        </Form.Item>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Form.Item
          name="amount"
          label="Amount"
          rules={[{ required: true, message: "Please enter amount" }]}
        >
          <InputNumber
            disabled={viewMode}
            placeholder="Enter amount"
            min={0.01}
            className="w-full"
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item name="transactionDate" label="Transaction Date">
          <DatePicker
            disabled={viewMode}
            className="w-full"
            style={{ width: "100%" }}
            placeholder="Select date"
          />
        </Form.Item>
      </div>

      <Form.Item name="transactionId" label="Transaction ID">
        <Input disabled={viewMode} placeholder="Enter transaction ID (optional)" />
      </Form.Item>

      <Form.Item name="note" label="Note">
        <Input.TextArea disabled={viewMode} placeholder="Enter note (optional)" rows={3} />
      </Form.Item>

      {!viewMode && (
        <Form.Item>
          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={() => form.resetFields()}
              className="border border-purple-500 text-black flex items-center justify-center w-[50%] py-2 rounded-xl"
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-gradient flex items-center justify-center w-full py-2 "
            >
              {editData
                ? loading ? "Updating..." : "Update Voucher"
                : loading ? "Adding..." : "Add Voucher"}
            </button>
          </div>
        </Form.Item>
      )}
    </Form>
  );
};

const MainSection = () => {
  const dispatch = useDispatch();
  const { cashFlows, loading, total } = useSelector((s) => s.admin.cashFlow);
  const { entities: bankEntities } = useSelector((s) => s.admin.entity);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isOpen, setIsOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    dispatch(fetchCashFlowsThunk({ search: searchText, page: currentPage, limit: pageSize }));
  }, [dispatch, searchText, currentPage, pageSize]);

  useEffect(() => {
    dispatch(fetchEntity({ search: "Bank", page: 1, limit: 1000 }));
  }, [dispatch]);

  const handleSearch = (e) => {
    setCurrentPage(1);
    setSearchText(e.target.value);
  };

  const handleDelete = (id) => {
    dispatch(deleteCashFlowThunk(id));
  };

  const columns = [
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Type
        </span>
      ),
      dataIndex: "transactionType",
      key: "transactionType",
      render: (value) => {
        const opt = transactionTypeOptions.find((t) => t.value === value);
        return (
          <span
            className={`inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${txnColors[value] || "bg-gray-50 text-gray-600 border-gray-100"
              }`}
          >
            {opt?.label || value || "—"}
          </span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          From
        </span>
      ),
      dataIndex: "from",
      key: "from",
      render: (from) => {
        if (!from) return <span className="text-sm text-gray-400">—</span>;
        const entityName = from.entityId?.entityName;
        return (
          <div className="flex flex-col gap-0.5">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border w-fit ${typeColors[from.type] || "bg-gray-50 text-gray-600 border-gray-100"
                }`}
            >
              {typeLabels[from.type] || from.type || "—"}
            </span>
            {entityName && (
              <span className="text-xs text-gray-500">{entityName}</span>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          To
        </span>
      ),
      dataIndex: "to",
      key: "to",
      render: (to) => {
        if (!to) return <span className="text-sm text-gray-400">—</span>;
        const entityName = to.entityId?.entityName;
        return (
          <div className="flex flex-col gap-0.5">
            <span
              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border w-fit ${typeColors[to.type] || "bg-gray-50 text-gray-600 border-gray-100"
                }`}
            >
              {typeLabels[to.type] || to.type || "—"}
            </span>
            {entityName && (
              <span className="text-xs text-gray-500">{entityName}</span>
            )}
          </div>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Amount
        </span>
      ),
      dataIndex: "amount",
      key: "amount",
      render: (value) => (
        <span className="text-sm font-semibold text-gray-800">
          {value != null ? Number(value).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Date
        </span>
      ),
      dataIndex: "transactionDate",
      key: "transactionDate",
      render: (value) => (
        <span className="text-sm text-gray-500">
          {value ? dayjs(value).format("DD MMM YYYY") : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Note
        </span>
      ),
      dataIndex: "note",
      key: "note",
      ellipsis: true,
      render: (value) => (
        <span className="text-sm text-gray-500">{value || "—"}</span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Action
        </span>
      ),
      key: "action",
      render: (_, record) => (
        <div className="flex items-center gap-1">
          <ProtectedAction requiredPermission={PERMISSIONS.EDIT_VOUCHERS}>
            <button
              onClick={() => {
                setEditData(record);
                setIsOpen(true);
              }}
              title="Edit"
              className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 transition"
            >
              <MdEdit size={16} />
            </button>
          </ProtectedAction>


          <button
            onClick={() => {
              setEditData({ mode: "view", ...record });
              setIsOpen(true);
            }}
            title="View"
            className="p-1.5 rounded-lg text-gray-400 hover:text-purple-500 hover:bg-purple-50 transition"
          >
            <MdRemoveRedEye size={16} />
          </button>
          <ProtectedAction requiredPermission={PERMISSIONS.DELETE_VOUCHERS}>
            <Popconfirm
              title="Delete Voucher"
              description="Are you sure you want to delete this voucher?"
              onConfirm={() => handleDelete(record._id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <button
                title="Delete"
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
              >
                <MdDelete size={16} />
              </button>
            </Popconfirm>
          </ProtectedAction>

        </div>
      ),
    },
  ];

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-xl p-4 shadow-sm mb-4 flex justify-between items-center">
        <div>
          <h1 className="page-heading w-52">Vouchers</h1>
          <p className="subheading">
            Manage cash flow Vouchers, transactions, and related configurations efficiently.
          </p>
        </div>
        <ProtectedAction requiredPermission={PERMISSIONS.ADD_VOUCHERS}>
          <button
            onClick={() => {
              setEditData(null);
              setIsOpen(true);
            }}
            className="inline-flex items-center gap-2 pl-4 border border-gray-200 rounded-full bg-white hover:shadow-md transition duration-200 shadow-sm flex-shrink-0"
          >
            <span className="text-sm font-semibold text-gray-800">Add Voucher</span>
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-600 to-violet-600 flex items-center justify-center text-white flex-shrink-0">
              <FaPlusCircle size={16} />
            </div>
          </button>
        </ProtectedAction>

      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 flex items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <CiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search"
            value={searchText}
            onChange={handleSearch}
            className="w-full pl-9 pr-4 py-2 text-sm bg-transparent border-0 focus:outline-none text-gray-700 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <TableShimmer columns={8} rows={10} />
        ) : (
          <Table
            dataSource={cashFlows}
            columns={columns}
            rowKey="_id"
            rowClassName="hover:bg-gray-50 transition-colors"
            components={{
              body: {
                row: (props) => <tr {...props} style={{ height: "60px" }} />,
              },
            }}
            size="small"
            pagination={{
              current: currentPage,
              total: total,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50"],
              showTotal: (total) => `Total ${total} records`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
                dispatch(fetchCashFlowsThunk({ search: searchText, page, limit: size }));
              },
            }}
          />
        )}
      </div>

      {/* Sidebar */}
      <Sidebar
        title={
          editData?.mode === "view"
            ? "Voucher Details"
            : editData
              ? "Edit Voucher"
              : "Add Voucher"
        }
        width="45%"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <VoucherForm
          onClose={() => setIsOpen(false)}
          editData={editData}
          bankEntities={bankEntities}
        />
      </Sidebar>
    </div>
  );
};

export default MainSection;
